// Story 1.24 — first creator-status-gated SPA route guard.
//
// Wraps /account/creator-portfolios (and any future approved-creator-
// only SPA routes). Reads the session via useAccountMe(); while the
// query is pending, renders a spinner placeholder; on error or
// creator_status !== "approved", redirects with a toast.
//
// Mirror of AdminGuard.tsx shape (same useAccountMe hook; same
// useEffect-redirect pattern). The backend's enforce_creator_status_
// is_approved (server/src/routes/creators.rs::359, bumped to
// pub(crate) in Story 1.24) is the real authorization boundary —
// every approved-creator-only API endpoint enforces it.
//
// Suspended creators are redirected to /account/become-creator with a
// suspension-specific toast. NotApproved (none/applied/rejected/
// withdrawn) also go to /account/become-creator with the apply-first
// nudge.

import { useEffect, type ReactNode } from "react";
import toast from "react-hot-toast";
import { Navigate, useNavigate } from "react-router-dom";

import {
  hasCreatorPageAccess,
  useAccountMe,
} from "../features/admin/useAccountMe";

type Props = {
  children: ReactNode;
};

export function ApprovedCreatorGuard({ children }: Props) {
  const navigate = useNavigate();
  const query = useAccountMe();

  useEffect(() => {
    if (query.isError) {
      toast.error("You need to be signed in.");
      navigate("/", { replace: true });
      return;
    }
    if (!query.data) return;
    // FORMER creators (withdrew consent → status "none" but
    // creator_approved_at set) keep access: their stats stay visible
    // and they can still withdraw from remaining portfolios. The
    // server's history-read gate is the real boundary.
    if (hasCreatorPageAccess(query.data.data)) return;
    const status = query.data.data.creator_status;
    if (status === "suspended") {
      toast.error("Your creator status is suspended. Contact support.");
    } else {
      toast(
        "Apply to become a creator first — this page is approved-creator only.",
      );
    }
    navigate("/account/become-creator", { replace: true });
  }, [query.isError, query.data, navigate]);

  if (query.isPending) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-paper-100 text-brand-forest-900">
        <p className="opacity-70 text-sm font-mono">Loading creator session…</p>
      </div>
    );
  }

  if (query.isError) {
    return <Navigate to="/" replace />;
  }

  if (!hasCreatorPageAccess(query.data?.data)) {
    return <Navigate to="/account/become-creator" replace />;
  }

  return <>{children}</>;
}
